/*
 * Copyright (c) 2022 CalAmp Corp.  All Rights Reserved
 */
import {ReactChild, ReactNode} from 'react';
import {createRoot, Root} from 'react-dom/client';
import isString from 'lodash.isstring';
import {error} from '@ion/core/src/logger/logger';
import {GenericObject} from '@ion/core/src/types/entities';

/**
 * Defines the pubic API to be implemented by all client Root Managers, be it in it's
 * entirety, or via a subset implementation appropriate for the given API.
 */
export interface ClientRootManager {
  render: (component: Renderable, container: Container) => void;
  unmount: (container: Container, remove?: boolean) => boolean;
  destruct: (removeOnly?: boolean) => boolean;
  hasRoots: () => boolean;
}

/**
 * Defines the allowed renderable component data types as per the React `createRoot` API.
 */
export type Renderable = ReactChild | Iterable<ReactNode>;

/**
 * Defines the root API container type to which a client root is to render components.
 */
export type Container = Element | DocumentFragment;

/**
 * @private
 */
type RootInstance = {
  container: Container,
  root: Root
}

/**
 * The `RootManager` provides an abstraction over the React `createRoot` API in order to
 * simplify implementations by providing management of multiple container nodes and the
 * root APIs which they are associated. This simplifies use of root APIs by encapsulating
 * root management within a unified API.
 *
 * In addition, the `RootManager` allows for safely invoking the `render` and `unmount`
 * functions of the `createRoot` API, providing a streamlined implementation which is
 * similar to that of the traditional `ReactDOM.render` implementation.
 *
 * **Note, the `RootManager` is only intended for direct use by non-JSX modules. when
 * working with components, implementations should always use the {@link useRootManager}
 * hook.**
 *
 * ```typescript
 *   import RootManager from '@ion/dls/src/helpers/RootManager';
 *
 *   // instantiate a new root manager which allows for managing a collection of dom
 *   // containers and their associated root APIs ....
 *   const roots = new RootManager();
 *
 *   ...
 *
 *   // retrieve an existing element, or create for the first time if it does not exist ...
 *   const container = RootManager.createContainer('some-container-id');
 *
 *   // render a component to the container node ...
 *   roots.render(<SomeComponent />, container);
 *
 *   ...
 *
 *   // when provided with a reference to the same node, subsequent render invocations
 *   // will result in the previously rendered component being removed and the new
 *   // component being rendered to the existing root ...
 *   root.render(<SomeOtherComponent />, container);
 *
 *   // check if roots have been added ...
 *   if (roots.hasRoots()) {
 *      ...
 *   }
 *
 *   // unmount can safely be invoked regardless of the underlying roots state ...
 *   root.unmount(container);
 *
 *   // remove all root containers and reset the collection of roots ...
 *   roots.destruct();
 *
 *   // unmount and remove all root containers and reset the collection of roots ...
 *   roots.destruct(false);
 *
 *   // roots have been removed ...
 *   roots.hasRoots(); // false
 * ```
 *
 */
export default class RootManager implements ClientRootManager {
  /**
   * Defines the collection of DOM containers (nodes) and their associated root APIs.
   */
  private _roots: RootInstance[] = [];

  /**
   * Provides a convenience abstraction over the `createRoot` render function which
   * allows for both creating the root based on the provided node (if not yet created)
   * and rendering / re-rendering components to a new or existing client root.
   *
   * ```typescript
   *
   *   // retrieve the existing element, or create it for the first time ...
   *   const container = RootManager.createContainer(id);
   *
   *   // render a component to the container node ...
   *   roots.render(component, container);
   *
   *   ...
   *
   *   // when provided with a reference to the same container, subsequent re-renders
   *   // will result in the currently rendered component being removed and the new
   *   // component being rendered to the existing root ...
   *   root.render(component, container);
   *
   * ```
   *
   * @param component The React Node which is to be rendered to the given root.
   * @param container The container element (DOM Node) to which the React Node
   *        is to be rendered. If the provide node has already been used to create
   *        a root, the existing root will re-render the specified component.
   */
  public render(component: Renderable, container: Container): void {
    let instance = this.getRoot(container);
    if (!instance) {
      instance = {
        root : createRoot(container),
        container
      };
      this._roots.push(instance);
    }
    instance.root.render(component);
  }

  /**
   * Provides a wrapper method which safely delegates to the root `unmount` API, while
   * providing status regarding the action via the return.
   *
   * In addition to invoking `unmount` on the client root, by default, the container
   * node associated with the client root will also be removed from the dom. This
   * default behavior can be overridden by passing `false` to the `remove` parameter.
   *
   * ```typescript
   *   // root unmount can safely be invoked regardless of the underlying root's state.
   *   // returns true if a root exists for the provided container, otherwise, returns
   *   // false ...
   *   root.unmount(container);
   *
   * ```typescript
   *   // unmount the root and remove, but do not remove the associated container ...
   *   root.unmount(container, false);
   * ```
   *
   * @param   node The container element (DOM Node) to which a given React Node has
   *          previously been rendered.
   * @param   remove When `true` (default), the container element will be removed
   *          from the DOM.
   * @returns true if a root exists for the provided container, otherwise false.
   */
  public unmount(container: Container, remove = true): boolean {
    if (container) {
      const instance = this.getRoot(container);
      if (instance?.root) {
        instance.root.unmount();
        remove && RootManager.removeContainer(instance.container);
        return true;
      }
    }
    return false;
  }

  /**
   * Provides a convenience method which allows for determining if the `RootManager`
   * instance currently contains any client roots associated with it.
   *
   * ```typescript
   *   // create a new RootManager instance ...
   *   const roots = new RootManager();
   *
   *   // roots have yet to have been added ...
   *   roots.hasRoots(); // false
   *
   *   // retrieve an existing container, or create it for the first time ...
   *   const container = RootManager.createContainer(id);
   *
   *   // render a component to the container node ...
   *   roots.render(component, container);
   *
   *   // roots have been added ...
   *   roots.hasRoots(); // true
   *
   *   // unmount and remove all roots ...
   *   roots.destruct();
   *
   *   // roots have been removed ...
   *   roots.hasRoots(); // false
   *
   * ```
   *
   * @returns true if the `RootManager` instance currently has client roots.
   */
  public hasRoots(): boolean {
    return this._roots.length > 0;
  }

  /**
   * The `destruct` method is provided for memory management purposes in order to allow
   * clients to unmount and nullify all root instances.
   *
   * Generally, components need not invoke the `destruct` method directly as components
   * are to use the {@link useRootManager} hook, which invokes `destruct` by default when
   * the component unmounts.
   *
   * ```typescript
   *   const roots = new RootManager();
   *
   *   useEffect(() => {
   *     roots.destruct();
   *   }, [])
   * ```
   *
   * @param   removeOnly When `true` (default), the `destruct` method will be invoked
   *          to only remove the container node from the dom (and not invoke unmount
   *          on the given root). This is the default behavior and should be used for
   *          the majority of use-cases as React will natively manage the unmounting
   *          of roots when the parent component unmounts.
   * @returns `true` if all roots have been successfully removed from the `RootManager`,
   *          otherwise, `false`. Generally, this method should never fail; however, an
   *          error can potentially occur while invoking `unmount` on a root if the root
   *          has already been removed outside of the `RootManager` API.
   */
  destruct(removeOnly = false): boolean {
    try {
      this._roots.forEach((instance: RootInstance) => {
        if (!removeOnly) {
          instance.root.unmount?.();
        }
        RootManager.removeContainer(instance.container);
        instance.container = null;
        instance.root = null;
        instance = null;
      });
      this._roots = [];
    } catch (err) {
      error(`An Exception occurred while attempting to destruct all roots. ${err.toString()}`);
      return false;
    }
    return true;
  }

  /**
   * Provides a utility which determines if the specified node has already been added
   * and has a root instance associated with it, and, if so, returns the `RootInstance`,
   * otherwise returns `undefined`.
   *
   * @param   node The container element (DOM Node) to which the React Node is to be
   *          rendered.
   * @returns The `RootInstance` associated with the given node, otherwise, undefined.
   */
  private getRoot(node: Container): RootInstance | undefined {
    return this._roots.find(({container}: RootInstance) => container === node);
  }

  /**
   * Provides a utility for creating a root container element. This method should be used
   * by clients when creating a root API via the `RootManager` or via the native `createRoot`
   * API. If invoked with an element id, this method will first determine if the element
   * already exists in the DOM and, if so, it will be returned, otherwise, the element
   * will be created and returned.
   *
   * ```typescript
   *
   *   // retrieve the element for the id, or create it for the first time ...
   *   const container = RootManager.createContainer(id);
   *
   *   // render a component to a specific container (element/node) ...
   *   roots.render(component, container);
   *
   *   ...
   *
   *   // create a new container element / node with the given attributes ...
   *   const container = RootManager.createContainer({
   *     id       : 'some-id',
   *     name     : 'some-name',
   *     disabled : ''
   *   });
   *
   *   // render a component to a specific container node ...
   *   roots.render(component, container);
   *
   * ```
   *
   * @see https://developer.mozilla.org/en-US/docs/Web/API/Element/setAttribute
   *
   * @param   attributes Optional element id, or a map of element attributes and their
   *          respective values. Any valid native `setAttribute` key and value can be
   *          provided.
   * @param   el The native element that is to be created, defaults to 'div'.
   * @returns The newly created element based on the provided arguments.
   */
  public static createContainer(attributes?: string | GenericObject, el = 'div'): HTMLElement {
    const useId     = isString(attributes);
    const container = (useId && document.getElementById(attributes as string)) || document.createElement(el);
    if (attributes) {
      if (!useId) {
        Object.entries(attributes).forEach(([attr, val]) => {
          container.setAttribute(attr, val);
        });
      } else if (container.getAttribute('id') !== attributes as string) {
        container.setAttribute('id', attributes as string);
      }
    }
    return container;
  }

  /**
   * Provides an abstraction over the DOM API for retrieving a container element by
   * `id`.
   *
   * ```typescript
   *   // retrieve a container element by id ...
   *   const container = RootManager.getContainer(id);
   * ```
   *
   * @see https://developer.mozilla.org/en-US/docs/Web/API/Document/getElementById
   *
   * @param   id The identifier (id) of the container element to be retrieved.
   * @returns The container element by id if available in the underlying DOM, otherwise,
   *          returns `null`.
   */
  public static getContainer(id: string): HTMLElement | null {
    return document.getElementById(id);
  }

  /**
   * Provides a convenience method which safely removes a container element from
   * the DOM.
   *
   * ```typescript
   *   // remove a container element from the dom ...
   *   RootManager.removeContainer(RootManager.getContainer(id));
   * ```
   *
   * @see https://developer.mozilla.org/en-US/docs/Web/API/Element/remove
   *
   * @param container The container element (DOM Node) to be removed from the DOM.
   */
  public static removeContainer(container: Container): void {
    (container as HTMLElement)?.remove?.();
  }

  /**
   * Finds an element's parent whose property matches the given target.
   * May return the given element if its property matches.
   * 
   * ```typescript
   * 
   *   // Finds a parent by its aria role description
   *   return RootManager.getParentElementByProperty(element, 'ariaRoleDescription', target);
   * 
   * ```
   * 
   * @param   element      The element whose parents are to be searched.
   * @param   propertyName Name of the property to check.
   * @param   target       Property value to check against.
   * @returns The first parent element, which may be the given element, whose
   *          property matches the given target.
   */
  public static getParentElementByProperty(element: HTMLElement, propertyName: string, target: string): HTMLElement {
    while (element && element[propertyName] !== target) {
      element = element.parentElement;
    }
    return element;
  };
};
